import * as React from 'react'

import { useClientAccountEdit } from './hooks'
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CForm,
  CFormCheck,
  CFormSelect,
  CNav,
  CNavItem,
  CNavLink,
  CRow,
  CTabContent,
  CTabPane,
} from '@coreui/react-pro'
import { useState } from 'react'
import { ClientAccount, Error } from './types'
import { BalanceOperationsWidget } from '../widgets/balanceOperations'
import {
  setClientAccountEdit,
  submitSwitchSwapfreeAccount,
  submitEditClientAccountLeverage,
  submitArchiveAccount,
} from './model'
import { leverages } from '../../../libs/leverages'
import { Divider, Tag } from 'antd'
import { formatDate } from '../../../libs/formatDates'

type Props = {
  className?: string
}

export const ClientAccountEdit = ({ className }: Props) => {
  const { clientAccountEdit, loading, error } = useClientAccountEdit()
  const [activeAccountTab, setActiveAccountTab] = useState(1)

  return (
    <div>
      <CRow>
        <CCol xs={10}>
          <CNav variant="tabs" role="tablist">
            <CNavItem>
              <CNavLink
                data-tab="detail"
                href="#!"
                active={activeAccountTab === 1}
                onClick={() => setActiveAccountTab(1)}
              >
                Detail
              </CNavLink>
            </CNavItem>
            <CNavItem>
              <CNavLink
                data-tab="balanceOperations"
                href="#!"
                active={activeAccountTab === 2}
                onClick={() => setActiveAccountTab(2)}
              >
                Balance operations
              </CNavLink>
            </CNavItem>
            <CNavItem>
              <CNavLink
                data-tab="tradingOperations"
                href="#!"
                active={activeAccountTab === 3}
                onClick={() => setActiveAccountTab(3)}
              >
                Trading operations
              </CNavLink>
            </CNavItem>
          </CNav>
        </CCol>
        <CCol className=" d-flex align-items-baseline justify-content-end">
          {(clientAccountEdit.accountType == 'ACCOUNT_TYPE_REAL' ||
            clientAccountEdit.accountType == 'real') &&
            !clientAccountEdit.active && (
              <CButton
                color="danger"
                className="me-2"
                title={'Archive'}
                onClick={() => submitArchiveAccount({ status: 'archived' })}
                disabled={loading}
              >
                {loading && (
                  <span className="spinner-border spinner-border-sm"></span>
                )}
                Archive
              </CButton>
            )}
        </CCol>
      </CRow>
      <CTabContent className="pt-3">
        <CTabPane
          data-tab="detail"
          role="tabpanel"
          aria-labelledby="contact-tab"
          visible={activeAccountTab === 1}
        >
          <CRow>
            <ClientAccountDetail
              className={className}
              account={clientAccountEdit}
              loading={loading}
              error={error}
            ></ClientAccountDetail>
          </CRow>
        </CTabPane>
        <CTabPane
          data-tab="balanceOperations"
          role="tabpanel"
          aria-labelledby="contact-tab"
          visible={activeAccountTab === 2}
        >
          <CRow>
            <AccountBalanceOperations
              className={className}
              account={clientAccountEdit}
            ></AccountBalanceOperations>
          </CRow>
        </CTabPane>
        <CTabPane
          data-tab="tradingOperations"
          role="tabpanel"
          aria-labelledby="contact-tab"
          visible={activeAccountTab === 3}
        >
          <CRow>
            <AccountTradingOperations
              className={className}
              account={clientAccountEdit}
            ></AccountTradingOperations>
          </CRow>
        </CTabPane>
      </CTabContent>
    </div>
  )
}

type AccountDetailProps = {
  className?: string
  loading?: boolean
  account: ClientAccount
  error: Error
}

const ClientAccountDetail = ({
  className,
  account,
  loading,
  error,
}: AccountDetailProps) => {
  const handleChange = (event: {
    currentTarget: { name: any; type: string; checked?: any; value: any }
  }) => {
    const value =
      event.currentTarget.type === 'checkbox'
        ? event.currentTarget.checked
        : event.currentTarget.value

    setClientAccountEdit({
      ...account,
      [event.currentTarget.name]: value,
    })
  }
  return (
    <CCol xs={12} className={className}>
      <CCard className="mb-4">
        <CCardBody>
          <CRow>
            {error.message.length > 0 && (
              <CAlert color="danger">Error code: {error.message}</CAlert>
            )}
          </CRow>
          <CRow>
            <CCol sm={6}>
              <p>Account status: {account.status}</p>
              <p>Archiving type: {account.archivingType}</p>
              <p>
                Archived at:{' '}
                {account.archivedAt ? formatDate(account.archivedAt) : ''}
              </p>
              <p>Account id: {account.accountId}</p>
              <p>User id: {account.clientId}</p>
              <p>Account name: {account.accountName}</p>
              <p>MT Login: {account.mtLogin}</p>
              <p>MT Comment: {account.mtComment}</p>
              <p>accountGroup :{account.group}</p>
              <p>serverId: {account.serverId}</p>
              <p>accountPlatform :{account.accountPlatform}</p>
              <p>availableForWithdrawal :{account.availableForWithdrawal}</p>
              <p>accountType :{account.accountType}</p>
              <p>tariff :{account.tariff}</p>
              <p>accCreatedAt :{account.accCreatedAt}</p>
              <p>accUpdatedAt :{account.accUpdatedAt}</p>
              <p>balance :{account.balance}</p>
              <p>currency :{account.currency}</p>
              <p>equity :{account.equity}</p>
              <p>
                isDemo:{' '}
                <Tag color={account.isDemo ? 'success' : 'error'}>
                  {account.isDemo ? 'Yes' : 'No'}
                </Tag>
              </p>
              <p>
                isFixRate:{' '}
                <Tag color={account.isFixRate ? 'success' : 'error'}>
                  {account.isFixRate ? 'Yes' : 'No'}
                </Tag>
              </p>
              <p>
                isSwapFree:{' '}
                <Tag color={account.isSwapFree ? 'success' : 'error'}>
                  {account.isSwapFree ? 'Yes' : 'No'}
                </Tag>
              </p>
              <p>leverage :{account.leverage}</p>
              <p>margin :{account.margin}</p>
              <p>marginFree :{account.marginFree}</p>
            </CCol>
            <CCol sm={6}>
              <CRow>
                <CCol xs={12}>
                  <CForm>
                    <CRow>
                      <CCol xs={12}>
                        <div className="mb-3">
                          <CFormCheck
                            id="isSwapFree"
                            name="isSwapFree"
                            checked={account.isSwapFree}
                            onChange={handleChange}
                            label="Swap free"
                          />
                        </div>
                      </CCol>
                    </CRow>
                    <CButton
                      className="mt-3"
                      disabled={loading}
                      onClick={() =>
                        submitSwitchSwapfreeAccount({
                          isSwapFree: account.isSwapFree,
                        })
                      }
                    >
                      {loading && (
                        <span className="spinner-border spinner-border-sm"></span>
                      )}
                      Save
                    </CButton>
                    <Divider />
                  </CForm>
                </CCol>
              </CRow>
              <CRow>
                <CCol xs={12}>
                  {(account.accountType == 'ACCOUNT_TYPE_REAL' ||
                    account.accountType == 'real') && (
                      <CForm>
                        <CRow>
                          <CCol xs={12}>
                            <div className="mb-3">
                              <CFormSelect
                                label="leverage limit"
                                name="leverageLimit"
                                placeholder={'Choose leverage'}
                                value={account.leverageLimit}
                                onChange={handleChange}
                              >
                                <option>Choose leverage</option>
                                {leverages.map((item) => (
                                  <option value={item.value}>{item.text}</option>
                                ))}
                              </CFormSelect>
                            </div>
                          </CCol>
                          <CCol xs={12}>
                            <CFormCheck
                              id="isChangeMTLeverage"
                              name="isChangeMTLeverage"
                              checked={account.isChangeMTLeverage}
                              onChange={handleChange}
                              label="Update the leverage to the maximum available, taking into account the limitation"
                            />
                          </CCol>
                        </CRow>
                        <CButton
                          className="mt-3"
                          disabled={loading}
                          onClick={() =>
                            submitEditClientAccountLeverage({
                              leverageLimit: account.leverageLimit,
                              isChangeMTLeverage: account.isChangeMTLeverage,
                            })
                          }
                        >
                          {loading && (
                            <span className="spinner-border spinner-border-sm"></span>
                          )}
                          Save leverage
                        </CButton>
                      </CForm>
                    )}
                </CCol>
              </CRow>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
    </CCol>
  )
}

type BalanceOperationsProps = {
  className?: string
  account: ClientAccount
}

const AccountBalanceOperations = ({
  className,
  account,
}: BalanceOperationsProps) => {
  return (
    <CCol xs={12} className={className}>
      <CCard className="mb-4">
        <CCardBody>
          <CRow>
            <CCol>
              <BalanceOperationsWidget />
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
    </CCol>
  )
}
type TradingOperationsProps = {
  className?: string
  account: ClientAccount
}

const AccountTradingOperations = ({
  className,
  account,
}: TradingOperationsProps) => {
  return (
    <CCol xs={12} className={className}>
      <CCard className="mb-4">
        <CCardBody>
          <CRow>
            <CCol>
              <a
                target="_blank"
                href="https://h2o-team.atlassian.net/wiki/spaces/HEADLINER/pages/13795331/HWS+CRM+Clients#2.2.2.2.-%D0%A2%D0%BE%D1%80%D0%B3%D0%BE%D0%B2%D1%8B%D0%B5-%D0%BE%D0%BF%D0%B5%D1%80%D0%B0%D1%86%D0%B8%D0%B8"
              >
                HWS CRM: Clients 2.2.2.2. Торговые операции
              </a>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
    </CCol>
  )
}
